import React, {useCallback, useState} from 'react';

import UnicodeCharacterChooser from 'notebook/mediocre/symbols/UnicodeCharacterChooser';
import Modal from 'ui-library/modals/Modal';

import isCharBlocked from './isCharBlocked';

interface InsertSymbolModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (symbol: string) => void;
}

function InsertSymbolModal({onRequestClose, onRequestSubmit}: InsertSymbolModalProps): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [symbol, setSymbol] = useState<string | null>(null);

  const onSubmit = useCallback(() => {
    if (symbol == null || isSubmitting) {
      // If there's no symbol, don't submit the modal. The footer button should be disabled, but there are
      // various ways to submit the form (in Safari, pressing enter on the text field submits the form, even
      // if the submit button is disabled).
      return;
    }
    setIsSubmitting(true);
    onRequestSubmit(symbol);
    onRequestClose();
  }, [symbol, isSubmitting, onRequestSubmit, onRequestClose]);

  const onSelectSymbol = useCallback(
    ({char, insert}: Parameters<React.ComponentProps<typeof UnicodeCharacterChooser>['selectChar']>[0]) => {
      if (isCharBlocked(char) || isSubmitting) {
        return;
      }
      setSymbol(char);
      if (insert) {
        onSubmit();
      }
    },
    [isSubmitting, setSymbol, onSubmit]
  );

  return (
    <Modal
      title="Insert symbol"
      onRequestClose={onRequestClose}
      onRequestSubmit={onSubmit}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          disabled: symbol == null || isSubmitting,
        },
      ]}
    >
      <UnicodeCharacterChooser char={symbol} selectChar={onSelectSymbol} />
    </Modal>
  );
}

export default InsertSymbolModal;
